import StandardAutoCompleteSearch, {
  AutoCompleteOption,
  AutoCompleteResultsZIndexPlacement,
} from '@components/standardAutoCompleteSearch/StandardAutoCompleteSearch'
import { TenantMemberDisplayData } from '@kudos/http-client'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { SxProps } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import UserAvatar from '@shared/avatar/UserAvatar'
import UserNameDisplay, { getUserDisplayName } from '@shared/userDisplay/UserNameDisplay'
import { t } from '@transifex/native'
import { useT } from '@transifex/react'
import { ReactNode, useEffect, useMemo, useState } from 'react'
import {
  Control,
  Controller,
  FieldName,
  FieldPathValue,
  FieldValues,
  Path,
  useFormContext,
  useWatch,
} from 'react-hook-form'
import ErrorText from '../ErrorText'
import FieldLabel from '../FieldLabel'
import HelpText from '../HelpText'
import useSearchTenantMembers, { TenantMemberSearchFn } from './useSearchTenantMembers'

export const renderTenantMemberOptions = (
  props: object,
  option: AutoCompleteOption<TenantMemberDisplayData>,
) => {
  return (
    <Stack {...props} key={option.id} direction="row" useFlexGap gap={1} width="100%">
      {option.avatar}
      <Stack minWidth={0}>
        <Typography
          my={0}
          minWidth={0}
          whiteSpace="nowrap"
          overflow="hidden"
          translate="no"
          textOverflow="ellipsis">
          <UserNameDisplay user={option.value} />
        </Typography>
        <Typography
          minWidth={0}
          variant="caption"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          mt={-0.5}>
          {option.value.email}
        </Typography>
      </Stack>
    </Stack>
  )
}

type TenantMemberSearchFieldProps<T extends FieldValues> = {
  name: Path<T>
  control: Control<T>
  searchFn: TenantMemberSearchFn
  label?: ReactNode
  labelStyle?: SxProps
  help?: ReactNode
  includeEmail?: boolean
  showAvatar?: boolean
  disabled?: boolean
  zIndexPlacement?: AutoCompleteResultsZIndexPlacement
  placeholder?: string
}
/** Allows search for single tenant member */
const TenantMemberSearchField = <T extends FieldValues>({
  name,
  control,
  searchFn,
  label,
  labelStyle,
  help,
  includeEmail = false,
  showAvatar = true,
  disabled = false,
  zIndexPlacement,
  placeholder,
}: TenantMemberSearchFieldProps<T>) => {
  const tNames = useT()
  const [searchText, setSearchText] = useState<string>('')
  const { setValue } = useFormContext()
  const [options, setOptions] = useState<AutoCompleteOption<TenantMemberDisplayData>[]>([])

  const selectedMembers = useWatch({ control, name })
  const selectedMembersIds = useMemo(
    () => selectedMembers?.map(({ id }: { id: string }) => id),
    [selectedMembers],
  )

  const { searchedTenantMembers, isFetching } = useSearchTenantMembers(
    searchText,
    selectedMembersIds,
    searchFn,
  )

  const onSelectedOptionsChanged = (options: AutoCompleteOption<TenantMemberDisplayData>[]) => {
    const ids = options.map(options => options.id)
    const newMembers: FieldPathValue<FieldValues, FieldName<FieldValues>> = [
      ...selectedMembers,
      ...(searchedTenantMembers ?? []),
    ].filter((value: { id: string }) => ids.includes(value.id))
    setValue(name, newMembers, { shouldValidate: true, shouldDirty: true })
  }

  useEffect(() => {
    setOptions(
      searchedTenantMembers?.map(member => ({
        value: member,
        id: member.id,
        avatar: showAvatar ? (
          <UserAvatar
            size={25}
            firstName={member.firstName}
            lastName={member.lastName}
            media={member.avatarUrls}
            avatarBoxStyle={{ display: 'flex' }}
          />
        ) : undefined,
      })) || [],
    )
  }, [searchedTenantMembers, includeEmail, showAvatar])

  const getValue = ():
    | AutoCompleteOption<TenantMemberDisplayData>
    | AutoCompleteOption<TenantMemberDisplayData>[] => {
    const member = selectedMembers[0] ?? null
    return (
      member &&
      ({
        id: member.id,
        value: member,
        avatar: (
          <UserAvatar
            size={25}
            firstName={member.firstName}
            lastName={member.lastName}
            media={member.avatarUrls}
            avatarBoxStyle={{ display: 'flex' }}
          />
        ),
      } as AutoCompleteOption)
    )
  }
  return (
    <Stack>
      <Controller
        name={name}
        control={control}
        render={({ fieldState: { error } }) => (
          <>
            {!!label && (
              <FieldLabel htmlFor={name} labelStyle={{ mb: !!help ? 0 : 1, ...labelStyle }}>
                {label}
              </FieldLabel>
            )}
            {!!help && <HelpText sx={{ mb: 1 }}>{help}</HelpText>}
            <StandardAutoCompleteSearch
              searchText={searchText}
              value={getValue()}
              options={options}
              onSearchTextChanged={setSearchText}
              onSelectedOptionsChanged={onSelectedOptionsChanged}
              loading={isFetching}
              placeholder={
                selectedMembers?.[0] ? '' : (placeholder ?? t('Search for a single user...'))
              }
              zIndexPlacement={zIndexPlacement}
              renderChip={(option, props) => {
                const { key, onDelete: _onDelete, ...otherProps } = props
                return (
                  <Box
                    key={key}
                    {...otherProps}
                    translate="no"
                    sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {option.avatar}
                    {includeEmail && option.value.email ? (
                      <Typography
                        component="span"
                        variant="body2"
                        minWidth={0}
                        noWrap
                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {`${getUserDisplayName(option.value, tNames)} (${option.value.email})`}
                      </Typography>
                    ) : (
                      <UserNameDisplay user={option.value} />
                    )}
                  </Box>
                )
              }}
              multiple={false}
              disabled={disabled}
              hideDropdown={true}
              readOnly={!!selectedMembers?.[0]}
              renderOption={renderTenantMemberOptions}
            />
            {!!error && <ErrorText>{error.message}</ErrorText>}
          </>
        )}
      />
    </Stack>
  )
}

export default TenantMemberSearchField
